/**
 * `authorize(ctx, action, resource)` — the role × resource matrix, in one place.
 *
 * §6 specifies *"permissions = role×resource matrix enforced in a single
 * `authorize(ctx, action, resource)` middleware"*. This is that matrix. Every
 * authorated route asks it, and nothing decides permission anywhere else — a second
 * place would be a second answer, and the two would disagree on the day it mattered.
 *
 * In `packages/shared` since U64, so `/settings/team` renders this table rather than a copy:
 * the dashboard may not import `apps/api`, even as a type — `apps/app/src/api/client.ts`.
 *
 * ## Deny by default, and it is structural rather than a habit
 *
 * {@link PERMISSIONS} lists the roles allowed each (resource, action) pair. **A pair
 * that is absent is denied**, so adding a resource or an action without deciding who may
 * use it fails closed — the new route 403s for everybody, loudly, in development, rather
 * than quietly allowing whoever happens to reach it. The alternative shape, a list of
 * denials, has the opposite failure: forget an entry and it is world-readable.
 *
 * `Resource` and `Action` are unions, and `PERMISSIONS` is typed as a total map over
 * `Resource`, so **adding a resource to the union without adding its row is a compile
 * error.** Adding an *action* is not, deliberately: most resources do not support most
 * actions, and requiring an explicit `[]` for every meaningless pair would be noise that
 * nobody reads and reviewers would rubber-stamp.
 *
 * ## Roles are not a ladder, which is why this is a matrix and not a rank comparison
 *
 * `planRank` exists for tiers because tiers *are* monotonic. Roles are not: the two
 * decisions below come from the design handoff's `screens.md` and neither can be
 * expressed by ordering.
 *
 *   - **An editor may publish a recall.** *"The person who finds out at 6pm is rarely
 *     the owner."* Safety actions are never gated (invariant 3) — not by tier, not by
 *     billing state, and not by seniority either.
 *   - **Retiring a product stops at admin.** An editor may create, update and publish
 *     products all day and may not take one out of circulation.
 *
 * An editor is therefore *more* privileged than an admin on one axis and less on
 * another. Any rank comparison would have to get one of them wrong.
 */
import { ROLES } from "./roles.js";
/**
 * What a request is trying to do.
 *
 * `manage` is deliberately absent. It reads as a convenience and it is an unbounded
 * grant: whoever adds the next capability to a resource inherits whatever `manage`
 * already meant, without anyone deciding. Every capability is named.
 */
export const ACTIONS = [
    "read",
    "create",
    "update",
    /**
     * Take something out of service **reversibly** — `links.active = false`.
     *
     * A distinct action rather than a flavour of `update`, ruled 2026-08-06. Two reasons,
     * and the second is the one that made it worth an entry in this union. It is the
     * capability that separates an editor from an admin on content, so it has to be
     * askable on its own — `requires("deactivate", "link")` says what it means, where
     * `requires("update", "link")` would silently carry the same permission as editing a
     * title. And the audit vocabulary is **user-facing** (U2): a customer reading their
     * own log should see `link.deactivate`, not `link.update` on a row whose diff they
     * have to interpret.
     */
    "deactivate",
    "delete",
    "publish",
    "retire",
];
/**
 * What it is trying to do it to.
 *
 * Phase 2's surface plus the resources §5 defines that Phase 2 writes to. Resources
 * arriving in later phases are added with their row, in the commit that adds them.
 */
export const RESOURCES = [
    "org",
    "member",
    "invite",
    "apiKey",
    "workspace",
    "product",
    "link",
    "recall",
    "artwork",
    "auditLog",
    "analytics",
    "billing",
    "domain",
    "ownership",
];
/** Everyone who is in the organization at all. Reading the catalogue is not a privilege. */
const EVERY_ROLE = ROLES;
/** The two roles that administer the organization itself. */
const ADMINS = ["owner", "admin"];
/** Those two plus the people who do the day-to-day catalogue work. */
const EDITORS = ["owner", "admin", "editor"];
/**
 * The matrix. Absent pair ⇒ denied.
 *
 * Typed as a total map over `Resource`: a new resource without a row will not compile.
 *
 * ## Which rows are derived, and which are this commit's judgement
 *
 * Worth marking, because a table-driven test cannot tell them apart. Sonnet made the
 * point while probing c27 and it is structural rather than fixable by writing the test
 * differently: an expectation table hand-typed from *this file* reproduces whatever
 * mistake this file makes. It is genuinely independent only where it can be checked
 * against something else.
 *
 * **Derived from a source outside this file — independently checkable:**
 *   - `recall.publish` = editors, and `product.retire` = admins (design handoff
 *     `screens.md` §10, quoted in the re-plan).
 *   - `auditLog.read` ungated (U2's ruling).
 *   - `org` has no `delete` (matches `0004`, which grants `app_rw` no DELETE on
 *     `organizations` — two mechanisms, same answer, and the database wins either way).
 *
 * **Ruled by Cameron 2026-08-06** — see `Decisions/2026-08-06 Permission Domains`. The
 * rows below were shipped as judgement and are now decided, and the ruling is a rule
 * about *domains* rather than about individual pairs:
 *
 * > *"Restrict only what can't be undone" refers to **content**, especially item
 * > deletion. Editors may not manage billing, invite people, manage API keys, or manage
 * > members. Owners/admins manage those things.*
 *
 * So **content** — `product`, `link`, `recall`, and the media and batches c29 adds — is
 * where reversibility decides who may act. **Privilege** — `member`, `invite`, `apiKey`,
 * `billing` — is owner/admin regardless of reversibility, because the question there is
 * not *"can we put it back?"* but *"who may change who can do things?"*
 *
 * Removing a member is trivially reversible and still not an editor's call. That case is
 * the boundary in one line, and it is why `member.delete` is `ADMINS` even though the
 * reversibility test alone would put it with the editors.
 *
 * `workspace` is the one row the ruling does not name. It is treated as privilege —
 * workspaces carry the membership scoping in `memberships.workspace_ids` — and remains
 * this file's judgement.
 */
export const PERMISSIONS = {
    // The organization itself. `delete` is absent on purpose and matches the database:
    // `0004` grants `app_rw` no DELETE on `organizations`, so a route for it could not
    // work even if the matrix allowed it. Two mechanisms, same answer.
    org: { read: EVERY_ROLE, update: ADMINS },
    // Membership and invitation management. An editor cannot invite, and cannot promote
    // themselves — the two together are what stop a compromised editor session becoming
    // an owner one.
    member: { read: EVERY_ROLE, create: ADMINS, update: ADMINS, delete: ADMINS },
    invite: { read: ADMINS, create: ADMINS, delete: ADMINS },
    // Machine credentials are an administrative act: an API key outlives the session that
    // created it and carries its own scopes (§4).
    apiKey: { read: ADMINS, create: ADMINS, delete: ADMINS },
    workspace: { read: EVERY_ROLE, create: ADMINS, update: ADMINS, delete: ADMINS },
    // The catalogue. Editors do the work; `retire` stops at admin — screens.md, above.
    product: {
        read: EVERY_ROLE,
        create: EDITORS,
        update: EDITORS,
        publish: EDITORS,
        retire: ADMINS,
    },
    // Links are resolution behaviour, which is why an editor may change them: getting a
    // link wrong is recoverable, and the resolver refuses the unrecoverable cases itself.
    //
    // **Ruled 2026-08-06: an editor may deactivate a link and may not delete one.**
    // `deactivate` flips `links.active` — reversible, and the everyday act, because a link
    // that is wrong should stop resolving in seconds rather than waiting for an admin.
    // `delete` removes the row and its history, which is not reversible, so it stops at
    // admin. This is the content principle in one row: reversibility decides.
    link: {
        read: EVERY_ROLE,
        create: EDITORS,
        update: EDITORS,
        deactivate: EDITORS,
        delete: ADMINS,
    },
    // **The safety carve-out.** `publish` is EDITORS by product decision, and there is no
    // tier gate, no billing gate and no throttle anywhere on this path (invariant 3).
    // `retire` here means lifting a recall, which is an administrative judgement about
    // whether the hazard has passed — a different act from raising the alarm.
    recall: { read: EVERY_ROLE, create: EDITORS, publish: EDITORS, retire: ADMINS },
    // c36 reads this. Ungated in Phase 2 (U2's recommendation): there are no paying tiers
    // before Phase 5, so a gate would enforce a price that does not exist yet. Analysts
    // and billing users can read it because "who changed what" is exactly the question a
    // non-editor asks. Nobody can write it from here — the writer is `audit.ts`, and the
    // database refuses UPDATE and DELETE to `app_rw` regardless (`0004`).
    auditLog: { read: EVERY_ROLE },
    // What the analyst role exists for.
    analytics: { read: EVERY_ROLE },
    // The one resource an analyst and an editor cannot see. Ruled 2026-08-06: editors may
    // not manage billing. `billing` is the role §4 created so a finance contact can reach
    // it without also being able to edit the catalogue — which is why this is
    // owner + billing rather than owner alone: owner-only would leave that role with no
    // capability at all. Flagged to Cameron as the one half of the ruling still to
    // confirm rather than derive.
    billing: { read: ["owner", "billing"], update: ["owner", "billing"] },
    // **Print files, and the row is `create` at EDITORS rather than `read` at everyone.**
    //
    // Generating artwork looks like a read — nothing about the catalogue changes — and it is
    // classed as a create for the reason §8.1 exists: printing is the one act in this product
    // that cannot be undone. The content principle the 2026-08-06 ruling states is
    // *"restrict only what can't be undone"*, and this is the most literal case of it
    // anywhere in the matrix. An analyst can read every product and cannot commit the
    // organisation to twelve thousand labels.
    //
    // It is also the row that makes `create` the honest verb: from c91b this writes a `media`
    // row recording what was generated, with what parameters, against which URI — the record
    // a recall's blast radius is later computed from.
    artwork: { create: EDITORS },
    // **Privilege, not content — ruled 2026-09-08.** Adding a domain commits the
    // organisation's DNS to our serving path, and once it serves once `first_live_at`
    // permanently unblocks printing against that hostname (`0016`, §8.1). That is "who may
    // change what the organisation is committed to", which the 2026-08-06 ruling puts on the
    // privilege side. Per-verb reversibility does not override the resource's domain — the
    // ruling's own boundary case is `member.delete`, trivially reversible and still ADMINS.
    //
    // `deactivate` rather than `delete`, because `0023` makes removal a status transition:
    // the row and `first_live_at` survive and re-adding the hostname revives it. A `delete`
    // that deletes nothing would put a false word in the customer's audit log.
    //
    // `update` is what `recheck` asks for. It moves `status` and `last_dns_answer`, and a
    // `recheck` member of ACTIONS would be a one-consumer synonym for what `update` means.
    domain: { read: EVERY_ROLE, create: ADMINS, update: ADMINS, deactivate: ADMINS },
    // Handing the organisation to another member. An owner only, and never plan-gated, or a
    // Launch sole owner could never leave — Decisions/2026-10-30 Handing Over an Organisation
    // Is Never a Paid Capability.
    ownership: { update: ["owner"] },
};
/**
 * May this caller perform this action on this resource?
 *
 * Pure, synchronous and total — no I/O, no throwing. The middleware turns `false` into
 * the 403; keeping that here would make the matrix untestable without a request.
 */
export function can(ctx, action, resource) {
    const allowed = PERMISSIONS[resource][action];
    return allowed !== undefined && allowed.includes(ctx.role);
}
//# sourceMappingURL=permissions.js.map